import React, {
  useMemo,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";

import type { ViewItemListEvent } from "types/events";

import { useGoogleTagManager } from "~/hooks/useGoogleTagManager";

export interface AnalyticsViewItemListProviderProps {
  children: React.ReactNode;
  /**
   * Path for current page.
   * Used for clearing the "duplicate" event check when the page changes.
   */
  pagePath: string;
}

interface AnalyticsViewItemListContextType {
  pushEvent: (event: AnalyticsViewItemListEvent) => void;
}

const AnalyticsViewItemListContext =
  createContext<AnalyticsViewItemListContextType | null>(null);

const eventBuffer = new Map<
  string, // item_list_id
  {
    events: AnalyticsViewItemListEvent[];
    timeoutId: ReturnType<typeof setTimeout> | null;
  }
>();

/*
 * Array of item_list_id::item_id strings used to prevent the same
 * item being emitted multiple times.
 */
let receivedItems: string[] = [];

export function AnalyticsViewItemListProvider({
  children,
  pagePath,
}: AnalyticsViewItemListProviderProps) {
  const googleTagManager = useGoogleTagManager();

  /** Emit the given group to Google Analytics */
  const emitGroup = useCallback(
    (item_list_id: string) => {
      // Get group with given item_list_id.
      const group = eventBuffer.get(item_list_id);
      if (!group || 0 === group.events.length) return;

      const firstEvent = group.events[0];
      if (!firstEvent) return;

      // Emit to Google Tag Manager.
      googleTagManager.push({
        event: "view_item_list",
        properties: {
          item_list_id: firstEvent.item_list_id,
          item_list_name: firstEvent.item_list_name,
          items: group.events.map((event) => event.item),
        },
      });

      // Remove the group from the buffer.
      eventBuffer.delete(item_list_id);
    },
    [googleTagManager],
  );

  // Context value.
  const value = useMemo<AnalyticsViewItemListContextType>(
    () => ({
      /** Push event to the buffer */
      pushEvent: (event) => {
        const { item_list_id } = event;

        // Compute a unique key for this event.
        const eventKey = `${item_list_id}::${event.item.item_id}`;

        // Check if event key has already been emitted.
        if (receivedItems.includes(eventKey)) return;
        else receivedItems.push(eventKey);

        // Group does not exist in buffer yet, create it.
        if (!eventBuffer.has(item_list_id)) {
          eventBuffer.set(item_list_id, {
            events: [],
            timeoutId: null,
          });
        }

        // Get group for item_list_id.
        const group = eventBuffer.get(item_list_id);
        if (!group) return;

        // Append event to group.
        group.events.push(event);

        // Clear any existing timeout for this group.
        if (group.timeoutId !== null) {
          clearTimeout(group.timeoutId);
        }

        // Set a new timeout (3 seconds).
        group.timeoutId = setTimeout(() => {
          emitGroup(item_list_id);
        }, 3000);

        // Check if the group size exceeds the limit (12).
        if (group.events.length >= 12) {
          // Clear the timeout since we're emitting now.
          clearTimeout(group.timeoutId);

          // Emit the group.
          emitGroup(item_list_id);
        }
      },
    }),
    [],
  );

  // When page path changes, emit anything left in the buffer, and clear received items.
  useEffect(() => {
    eventBuffer.forEach((_group, item_list_id) => emitGroup(item_list_id));
    receivedItems = [];
  }, [pagePath]);

  return (
    <AnalyticsViewItemListContext.Provider value={value}>
      {children}
    </AnalyticsViewItemListContext.Provider>
  );
}

export function useAnalyticsViewItemList() {
  return useContext(AnalyticsViewItemListContext)!;
}

/**
 * Google Analytics view_item_list event for a single product.
 * These events are grouped and sent together in a single event to Google Analytics.
 */
export type AnalyticsViewItemListEvent = Omit<
  ViewItemListEvent["properties"],
  "items"
> & {
  item: ViewItemListEvent["properties"]["items"][number];
};
